<template>
  <main class="min-h-screen bg-black text-white">
    <!-- HERO -->
    <section class="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div class="absolute inset-0 h-full w-full bg-gradient-hero z-0" />
      <div class="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1/2 bg-gradient-glow" />

      <div class="container mx-auto px-2 py-20 relative z-10">
        <div class="grid lg:grid-cols-2 items-center">
          <!-- LEFT -->
          <div class="text-center space-y-8 animate-fade-in flex flex-col items-center justify-center" style="margin-left:5%">
            <!-- ✨ Badge -->
            <div class="inline-flex items-center gap-2 px-2 py-2 rounded-full glass-card border border-white/10">
              <Sparkles class="w-4 h-4 text-[#3e71fd]" />
              <span class="text-sm text-gray-400">The Future of Networking</span>
            </div>

            <!-- 🧠 Heading -->
            <h1 class="text-5xl md:text-7xl font-bold leading-tight max-w-3xl">
              Your Professional Identity. <span class="text-[#3e71fd] glow-text">Reinvented.</span>
            </h1>

            <!-- 📄 Paragraph -->
            <p class="text-xl text-gray-400 max-w-xl mx-auto">
              Create, customize, and share stunning digital namecards — instantly via tap or QR.
            </p>

            <!-- 💬 Button (perfectly centered) -->
            <div class="flex justify-center w-full">
              <button
                @click="contactOnWhatsApp('Digital Namecard Inquiry')"
                class="bg-[#3e71fd] hover:bg-[#3e71fd]/90 text-white shadow-glow group px-8 py-3 rounded-lg font-semibold transition-all inline-flex items-center justify-center hover:shadow-lg hover:shadow-[#3e71fd]/30"
              >
                Contact Us
                <ArrowRight class="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            <!-- 🌍 Highlights -->
            <div class="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-gray-400 mt-4">
              <div class="flex items-center gap-2">
                <div class="w-2 h-2 rounded-full bg-[#3e71fd] animate-pulse" />
                <span>No app required</span>
              </div>
              <div class="flex items-center gap-2">
                <div class="w-2 h-2 rounded-full bg-[#3e71fd] animate-pulse" />
                <span>Eco-friendly</span>
              </div>
              <div class="flex items-center gap-2">
                <div class="w-2 h-2 rounded-full bg-[#3e71fd] animate-pulse" />
                <span>Always updated</span>
              </div>
            </div>
          </div>

          <!-- RIGHT -->
          <div class="relative animate-fade-in">
            <div class="absolute inset-0 bg-gradient-primary opacity-20 blur-3xl rounded-full" />
            <img
              :src="heroPhone"
              alt="Digital namecard on smartphone"
              class="relative z-10 w-full max-w-lg mx-auto animate-float rounded-2xl shadow-glow"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- WHY DIGITAL -->
    <section class="py-24 relative bg-black">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16 animate-fade-in">
          <h2 class="text-4xl md:text-5xl font-bold mb-4">
            Why Go <span class="text-primary">Digital?</span>
          </h2>
          <p class="text-xl text-gray-400 max-w-2xl mx-auto">
            Join thousands of professionals making the smart switch to digital networking
          </p>
        </div>

        <div class="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div
            v-for="(feature, index) in features"
            :key="index"
            class="glass-card p-8 rounded-2xl hover:shadow-glow transition-all duration-300 animate-fade-in"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <div class="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
              <component :is="feature.icon" class="w-7 h-7 text-primary" />
            </div>
            <h3 class="text-2xl font-semibold mb-3">{{ feature.title }}</h3>
            <p class="text-gray-400">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- HOW IT WORKS -->
    <section class="py-24 bg-gradient-to-b from-black via-gray-900 to-black">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16 animate-fade-in">
          <h2 class="text-4xl md:text-5xl font-bold mb-4">
            How It <span class="text-[#3e71fd]">Works</span>
          </h2>
          <p class="text-xl text-gray-400 max-w-2xl mx-auto">
            Get started in three simple steps
          </p>
        </div>

        <div class="max-w-5xl mx-auto">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="flex flex-col md:flex-row items-center gap-8 mb-12 last:mb-0 animate-fade-in"
            :style="{ animationDelay: `${index * 0.15}s` }"
          >
            <!-- 🔹 Blue box with white number -->
            <div class="relative flex-shrink-0">
              <div
                class="w-24 h-24 rounded-2xl bg-[#3e71fd] flex items-center justify-center border-2 border-[#3e71fd]/60"
              >
                <span class="text-4xl font-bold text-white">{{ step.number }}</span>
              </div>
              <div
                v-if="index < steps.length - 1"
                class="hidden md:block absolute top-full left-1/2 -translate-x-1/2 w-0.5 h-12 bg-gradient-to-b from-[#3e71fd]/70 to-transparent"
              />
            </div>

            <!-- Content -->
            <div class="glass-card p-8 rounded-2xl flex-1 text-left">
              <div class="flex items-center gap-4 mb-4">
                <div
                  class="w-12 h-12 rounded-xl bg-[#3e71fd]/10 flex items-center justify-center border border-[#3e71fd]/40"
                >
                  <component :is="step.icon" class="w-6 h-6 text-[#3e71fd]" />
                </div>
                <h3 class="text-2xl font-semibold">{{ step.title }}</h3>
              </div>
              <!-- 👇 Description aligned left -->
              <p class="text-gray-400 text-lg text-left">{{ step.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>





    <!-- SHOWCASE GALLERY -->
    <section class="py-24 relative bg-black">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16 animate-fade-in">
          <h2 class="text-4xl md:text-5xl font-bold mb-4">
            Showcase <span class="text-primary">Gallery</span>
          </h2>
          <p class="text-xl text-gray-400 max-w-2xl mx-auto">
            Beautiful templates for every industry
          </p>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          <div
            v-for="(card, index) in cards"
            :key="index"
            class="group relative glass-card p-8 rounded-2xl border-2 transition-all duration-300 cursor-pointer animate-fade-in"
            :class="card.border"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <div
              class="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"
              :class="`bg-gradient-to-br ${card.gradient}`"
            />
            <div class="relative z-10">
              <div class="w-16 h-16 rounded-xl bg-white/5 flex items-center justify-center mb-6 mx-auto">
                <component :is="card.icon" class="w-8 h-8 text-primary" />
              </div>
              <h3 class="text-2xl font-semibold text-center">{{ card.title }}</h3>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- LOVED BY PROFESSIONALS -->
    <!-- <section class="py-24 bg-gradient-to-b from-black via-gray-900 to-black">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16 animate-fade-in">
          <h2 class="text-4xl md:text-5xl font-bold mb-4">
            Loved by <span class="text-primary">Professionals</span>
          </h2>
        </div>

        <div class="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div
            v-for="(t, index) in testimonials"
            :key="index"
            class="glass-card p-8 rounded-2xl animate-fade-in"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <Quote class="w-10 h-10 text-primary/50 mb-6" />
            <p class="text-lg mb-6 text-gray-300">{{ t.quote }}</p>
            <div>
              <p class="font-semibold text-white">{{ t.name }}</p>
              <p class="text-sm text-gray-400">{{ t.role }}</p>
            </div>
          </div>
        </div>
      </div>
    </section> -->

    <!-- SIMPLE PRICING -->
    <section class="py-24 relative bg-black">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16 animate-fade-in">
          <h2 class="text-4xl md:text-5xl font-bold mb-4">
            Simple <span class="text-primary">Pricing</span>
          </h2>
          <p class="text-xl text-gray-400 max-w-2xl mx-auto">
            Choose the plan that fits your needs
          </p>
        </div>

        <div class="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div
            v-for="(tier, index) in tiers"
            :key="index"
            class="glass-card p-8 rounded-2xl relative animate-fade-in"
            :class="{ 'border-2 border-primary shadow-glow': tier.popular }"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <div
              v-if="tier.popular"
              class="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary rounded-full text-sm font-semibold text-white"
            >
              Most Popular
            </div>
            <h3 class="text-2xl font-bold mb-2">{{ tier.name }}</h3>
            <div class="mb-6">
              <span class="text-4xl font-bold">{{ tier.price }}</span>
              <span class="text-gray-400 ml-2">{{ tier.period }}</span>
            </div>
            <ul class="space-y-4 mb-8">
              <li
                v-for="(f, idx) in tier.features"
                :key="idx"
                class="flex items-start gap-3"
              >
                <Check class="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span class="text-gray-300">{{ f }}</span>
              </li>
            </ul>
            <button
              @click="contactOnWhatsApp(tier.name)"
              class="w-full px-6 py-3 rounded-lg font-semibold transition-all"
              :class="tier.popular
                ? 'bg-[#3e71fd] hover:bg-[#3e71fd]/90 text-white'
                : 'glass-card hover:bg-white/10'"
            >
              Contact Us
            </button>

          </div>
        </div>
      </div>
    </section>

    <!-- MEET EXACTFLO -->
    <section class="py-24 relative overflow-hidden bg-gradient-to-b from-black via-gray-900 to-black">
      <div class="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-primary/5" />
      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-4xl mx-auto text-center animate-fade-in">
          <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card border border-white/10 mb-8">
            <Zap class="w-4 h-4 text-primary" />
            <span class="text-sm text-gray-400">Introducing ExactFlo</span>
          </div>

          <h2 class="text-4xl md:text-6xl font-bold mb-6">
            Meet <span class="text-primary glow-text">ExactFlo</span><br />
            Automation for Modern Businesses
          </h2>

          <p class="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            ExactFlo, a product by The Network Tap, helps companies automate internal workflows —
            from HR and scheduling to lead management and reporting — powered by smart AI.
          </p>

          <div class="grid md:grid-cols-3 gap-6 mb-12 max-w-3xl mx-auto">
            <div
              v-for="(feature, index) in exactFloFeatures"
              :key="index"
              class="glass-card p-6 rounded-xl text-white"
              :style="{ animationDelay: `${index * 0.1}s` }"
            >
              <div class="w-2 h-2 rounded-full bg-primary mx-auto mb-3 animate-pulse" />
              <p class="text-sm font-medium text-gray-200">{{ feature }}</p>
            </div>
          </div>

          <button
            @click="goToExactFlo"
            class="bg-[#3e71fd] hover:bg-[#3e71fd]/90 text-white shadow-glow group px-6 py-3 rounded-lg font-semibold transition-all inline-flex items-center justify-center"
          >
            Learn More at ExactFlo.com
            <ArrowRight class="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>

        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="border-t border-gray-800 py-12 mt-24 bg-black text-gray-400">
      <div class="container mx-auto px-4">
        <div class="flex flex-col md:flex-row items-center justify-between gap-6">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <img
                  src="@/assets/TNT_LOGO.png"
                  alt="Logo"
                  class="max-w-[70%] max-h-[70%] invert brightness-200 object-contain"
                />
              </div>

            <span class="text-xl font-bold text-white">The Network Tap</span>
          </div>
          <div class="text-center md:text-left">
            <p class="text-sm mb-1">Products:</p>
            <div class="flex gap-4 text-sm">
              <span class="hover:text-primary transition-colors cursor-pointer text-white">
                Digital Namecard
              </span>
              <span class="text-gray-600">•</span>
              <a
                href="https://exactflo.com"
                target="_blank"
                rel="noopener noreferrer"
                class="hover:text-primary transition-colors text-white"
              >
                ExactFlo.com
              </a>
            </div>
          </div>
          <p class="text-sm text-gray-500">© 2023 The Network Tap. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </main>
</template>

<script>
import {
  ArrowRight,
  Sparkles,
  RefreshCw,
  Leaf,
  Users,
  Smartphone,
  Palette,
  Share2,
  Briefcase,
  Crown,
  ShoppingBag,
  Quote,
  Check,
  Zap,
} from "lucide-vue-next";
import heroPhone from "@/assets/hero-phone.jpg";

export default {
  name: "Index",
  components: {
    ArrowRight,
    Sparkles,
    RefreshCw,
    Leaf,
    Users,
    Smartphone,
    Palette,
    Share2,
    Briefcase,
    Crown,
    ShoppingBag,
    Quote,
    Check,
    Zap,
  },
  data() {
    return {
      heroPhone,
      features: [
        {
          icon: RefreshCw,
          title: "Always Up to Date",
          description:
            "No more reprints. Update your info instantly and everyone sees the latest version.",
        },
        {
          icon: Leaf,
          title: "Eco-Friendly & Modern",
          description:
            "Say goodbye to paper waste. Join the sustainable networking revolution.",
        },
        {
          icon: Users,
          title: "Perfect for Teams",
          description:
            "Ideal for professionals, sales teams, and businesses of all sizes.",
        },
      ],
      steps: [
        {
          icon: Smartphone,
          number: "01",
          title: "Choose Your Card Type",
          description:
            "Select from NFC tap cards, QR codes, or digital-only options.",
        },
        {
          icon: Palette,
          number: "02",
          title: "Customize Your Design",
          description:
            "Pick colors, add your logo, and personalize every detail to match your brand.",
        },
        {
          icon: Share2,
          number: "03",
          title: "Share Instantly",
          description:
            "Tap to share with NFC, scan a QR, or send your digital card via link.",
        },
      ],
      cards: [
        {
          icon: Briefcase,
          title: "Corporate",
          gradient: "from-blue-500/20 to-cyan-500/20",
          border: "border-blue-500/30",
        },
        {
          icon: Palette,
          title: "Creative",
          gradient: "from-purple-500/20 to-pink-500/20",
          border: "border-purple-500/30",
        },
        {
          icon: Crown,
          title: "Luxury",
          gradient: "from-amber-500/20 to-yellow-500/20",
          border: "border-amber-500/30",
        },
        {
          icon: ShoppingBag,
          title: "Retail",
          gradient: "from-green-500/20 to-emerald-500/20",
          border: "border-green-500/30",
        },
      ],
      testimonials: [
        {
          name: "Sarah Chen",
          role: "Sales Director",
          quote:
            "Switching to digital namecards has transformed how our team networks. No more running out at events.",
        },
        {
          name: "Marcus Williams",
          role: "CEO, Tech Startup",
          quote:
            "The customization options are incredible. Our cards perfectly match our brand identity.",
        },
        {
          name: "Lisa Rodriguez",
          role: "Marketing Manager",
          quote:
            "Eco-friendly, professional, and always up-to-date. This is the future of business cards.",
        },
      ],
      tiers: [
        {
          name: "Plastic Card",
          price: "$50",
          period: "one-time",
          features: [
            "1 NFC-enabled plastic card",
            "Unlimited Taps",
            "Real-time contact updates",
            "QR code sharing",
            "Premium customization",
            "Priority printing & delivery",
          ],
          popular: false,
        },
        {
          name: "Metal Card",
          price: "$80",
          period: "one-time",
          features: [
            "1 NFC-enabled metal card",
            "Unlimited Taps",
            "Real-time contact updates",
            "QR code sharing",
            "Premium customization",
            "Priority printing & delivery",
          ],
          popular: true, // make this your highlight plan
        },
        {
          name: "Custom Package",
          price: "Custom",
          period: "one-time",
          features: [
            "Bulk NFC cards (10+)",
            "Unlimited Taps",
            "Team branding & dashboard",
            "Real-time contact updates",
            "Full customization",
            "Dedicated account manager",
          ],
          popular: false,
        },
      ],
      exactFloFeatures: ["HR Automation", "Smart Scheduling", "Lead Management"],
    };
  },
  methods: {
    goToExactFlo() {
      window.open("https://exactflo.com", "_blank", "noopener,noreferrer");
    },
    contactOnWhatsApp(planName) {
      const phoneNumber = "6594768087"; // 🇸🇬 change to your actual WhatsApp number (no +)
      const message = encodeURIComponent(
        `Hi! I'm interested in the ${planName} package. Could you share more details?`
      );
      window.open(`https://wa.me/${phoneNumber}?text=${message}`, "_blank");
    },
  }

};
</script>

<style>
/* Dark base */
body { background-color: #000; color: #fff; }

/* 🌌 Hero gradient (full section) */
.bg-gradient-hero {
  background: radial-gradient(
      circle at 20% 30%,
      rgba(0, 0, 0, 0.85) 0%,
      rgba(0, 0, 0, 0.95) 40%,
      rgba(0, 0, 0, 1) 80%
    ),
    linear-gradient(180deg, #0a0a0a 0%, #111 100%);
}

/* ✨ Top glow */
.bg-gradient-glow {
  background: radial-gradient(
    circle at top center,
    rgba(59, 130, 246, 0.25),
    transparent 70%
  );
  pointer-events: none;
}

/* 🌈 Primary gradient blur */
.bg-gradient-primary {
  background: radial-gradient(circle at center, rgba(37, 99, 235, 0.3), transparent 70%);
}

/* 💫 Glass cards */
.glass-card {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* 🔆 Glow text */
.glow-text { text-shadow: 0 0 10px rgba(59, 130, 246, 0.5); }

/* 🌟 Glow shadow */
.shadow-glow { box-shadow: 0 0 25px rgba(59, 130, 246, 0.3); }

/* ✨ Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}
.animate-fade-in { animation: fadeIn 1s ease-in-out both; }

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-10px); }
}
.animate-float { animation: float 4s ease-in-out infinite; }
</style>
